import produce from 'immer';

import { CallTypes, RingPayload } from '../actions/calls';

export interface ActiveCall {
  callId: string;
  contactName: string;
  contactPhone: string;
  connectedAt: number;
  muted: boolean;
}

export interface CallsState {
  // Ringing inbound calls, keyed by call_id -- an account can have several at once
  incoming: Record<string, RingPayload>;
  // call_id of a claim in flight, null otherwise
  claiming: string | null;
  active: ActiveCall | null;
}

export const initialState: CallsState = {
  incoming: {},
  claiming: null,
  active: null,
};

export const reducer = produce((state: CallsState, action: any) => {
  switch (action.type) {
    case CallTypes.RING_RECEIVED: {
      const ring: RingPayload = action.payload.ring;
      // Ignore re-rings of the call we're already connected to (claim watchdog races)
      if (state.active?.callId === ring.call_id) break;
      state.incoming[ring.call_id] = ring;
      break;
    }
    case CallTypes.RING_STOPPED: {
      const { callId, reason } = action.payload;
      // A winning claim stops the ring for everyone, this browser included, while
      // answerCall is still joining the conference. Keep the prompt on
      // "Connecting…": the claim resolves it either way (CLAIM_WON or CLAIM_LOST,
      // also when someone else won instead).
      if (state.claiming === callId && reason === 'answered') break;
      delete state.incoming[callId];
      if (state.claiming === callId) state.claiming = null;
      break;
    }
    case CallTypes.RING_DISMISSED: {
      delete state.incoming[action.payload.callId];
      break;
    }
    case CallTypes.CLAIM_STARTED: {
      state.claiming = action.payload.callId;
      break;
    }
    case CallTypes.CLAIM_WON: {
      const { callId, contactName, contactPhone, connectedAt } = action.payload;
      state.claiming = null;
      delete state.incoming[callId];
      state.active = {
        callId,
        contactName,
        contactPhone,
        connectedAt,
        muted: false,
      };
      break;
    }
    case CallTypes.CLAIM_LOST: {
      const { callId } = action.payload;
      state.claiming = null;
      delete state.incoming[callId];
      break;
    }
    case CallTypes.CALL_ENDED: {
      if (state.active?.callId === action.payload.callId) {
        state.active = null;
      }
      break;
    }
    case CallTypes.MUTE_TOGGLED: {
      if (state.active) {
        state.active.muted = action.payload.muted;
      }
      break;
    }
  }
}, initialState);
